<script lang="ts">
export default {
  name: 'KkStoreModal',
}
</script>

<script lang="ts" setup>
import { computed } from 'vue'
import { KkFilterItem } from '../../base'
import Modal from '../../Modal.vue'
import type { AllFormValue } from 'src/biz_spa/types'
import type { SetFilterValue, ToggleFilterValue } from '../../base/KkFilterItem/index.vue'

export type Store = {
  attributes: { uuid: string, connected_gmb: boolean, external_accounts: [], name: string }
}

export type StoreModalFormValue = {
  stores: string[]
}

export type StoreModalToggleFilterValue = Omit<ToggleFilterValue, 'target' | 'initialValues'> & {
  target: 'stores';
  initialValues: string[];
}

export type StoreModalSetFilterValue = Omit<SetFilterValue, 'target' | 'value'> & {
  target: 'stores';
  value: string;
}

export interface Props {
  storeName: string
  searchStores: Store[]
  formValues: Partial<AllFormValue>
  storeTotalCount: number
  onlyGmbConnected?: boolean
}

export interface Emits {
  (key: 'back-filter-modal'): void
  (key: 'close-store-modal', value: string[]): void
  (key: 'set-form-values', value: StoreModalFormValue): void
  (key: 'toggle-filter-values', value: StoreModalToggleFilterValue): void
  (key: 'set-filter-values', value: StoreModalSetFilterValue): void
}

const props = withDefaults(defineProps<Props>(), {
  onlyGmbConnected: true,
})
const emits = defineEmits<Emits>()

const allStores = computed<{ uuid: string; name: string }[]>(() => {
  return props.searchStores.map((store) => ({ uuid: store.attributes.uuid, name: store.attributes.name }))
})

const allStoreUuids = computed(() => allStores.value.map((store) => store.uuid))

const setFormValues = (payload: StoreModalFormValue) => {
  emits('set-form-values', payload)
}

const toggleFilterValues = (payload: ToggleFilterValue) => {
  const onlyStringInitialValues = payload.initialValues.filter((value): value is Exclude<typeof value, number> => typeof value === 'string')

  emits('toggle-filter-values', { allChecked: payload.allChecked, initialValues: onlyStringInitialValues, target: 'stores' })
}

const setFilterValues = (payload: SetFilterValue) => {
  if(typeof payload.value !== 'string') return

  emits('set-filter-values', {  value: payload.value , target: 'stores' })
}

const cancelStoreModal = () => {
  setFormValues({ stores: [] })
  emits('back-filter-modal')
}

const confirmStoreModal = () => {
  emits('close-store-modal', props.formValues.stores || [])
}

if (props.formValues?.stores?.length === 0) {
  setFormValues({ stores: allStoreUuids.value })
}
</script>

<template>
  <Modal @close="cancelStoreModal()">
    <div class="p-modal__content__header">{{ $t('pages.CompaniesStorePost.pleaseSelectStores') }}</div>

    <div class="p-modal__content__body">
      <KkFilterItem
        :list="allStores"
        :list-type="'store'"
        value-key="uuid"
        :store-name="storeName"
        :head-text="$t('labels.selectAll')"
        filter-type="stores"
        :form-values="formValues"
        @set-filter-values="setFilterValues"
        @toggle-filter-values="toggleFilterValues"
      />
    </div>

    <div class="p-modal__content__footer">
      <div class="store-count-wap">{{ $t('labels.targetStoreCount') }}<span class="store-count">{{ storeTotalCount }}</span>{{ $t('labels.storeCountUnit') }}</div>
      <div class="c-button c-button--right">
        <button class="c-button__item c-button__item--outline-cancel" @click.prevent="cancelStoreModal()">{{ $t('labels.backSelectCondition') }}</button>
        <button class="c-button__item c-button__item--primary" @click.prevent="confirmStoreModal()">{{ $t('labels.submitFilterCond') }}</button>
      </div>
    </div>
  </Modal>
</template>

<style lang="scss" scoped>
@import "../../../../../stylesheets/biz_spa/foundation/_var.scss";
.p-modal {
  &__content {
    &__footer {
      padding: 12px 32px;
      border-top: 1px solid $color-base-c;
    }
  }
}
.store-count-wap {
  position: absolute;
  right: 270px;
  bottom: 5px;
}
.store-count {
  margin: 0 5px;
  color: $color-accent-primary;
  font-weight: 700;
  font-size: 30px;
}

</style>
